<template>
	<div>
		<Head :title="__('Products')" />

		<div class="mb-8 flex justify-between items-center">
			<h1 class="text-3xl font-bold">{{ __('Products') }}</h1>
			<Link class="btn-cta" href="/products/create">
				<span>{{ __('Create') }}</span>
				<span class="hidden md:inline">&nbsp;{{ __('product') }}</span>
			</Link>
		</div>

		<div class="mb-6 flex items-center justify-between">
			<div class="flex flex-col">
				<label class="mb-[10px]" for="cat_filter">Filtrera på kategori</label>

				<select
					v-model="form.category"
					id="cat_filter"
					name="cat_filter"
				>
					<option :value="null" selected="true">Välj kategori</option>

					<optgroup
						v-for="parent in parentCategories"
						:key="parent"
						:label="parent.name"
					>
						<option
							v-for="child in parent.childs"
							:key="child"
							:value="child.id"
						>
							{{ child.name }}
						</option>
					</optgroup>
				</select>
			</div>

			<SearchFilter
				v-model="form.search"
				class="mr-4 w-full max-w-md"
				:hasDropdown="false"
				@reset="reset"
			/>
		</div>

		<div class="overflow-x-auto rounded-md bg-white shadow">
			<table class="w-full whitespace-nowrap">
				<tr class="text-left font-bold">
					<th class="px-6 pb-4 pt-6"> {{ __('Name') }}</th>
					<th class="px-6 pb-4 pt-6">{{ __('Article Name') }}</th>
					<th class="px-6 pb-4 pt-6">{{ __('GCMA') }}</th>
					<th class="px-6 pb-4 pt-6"> {{ __('Published') }}</th>
				</tr>

				<tr v-for="product in products.data" :key="product.id" class="focus-within:bg-gray-100 hover:bg-gray-100">
					<td class="border-t">
						<Link class="flex items-start items-center px-6 py-4 focus:text-indigo-500" :href="`/products/${product.id}/edit`">
							<img
								:src="product.image_url ?? '/img/no-image.jpg'"
								class="mr-4 h-12 w-12 rounded-full"
							/>

							{{ product.name }}

							<Icon
								v-if="product.deleted_at"
								name="trash"
								class="ml-2 h-3 w-3 flex-shrink-0 fill-gray-400"
							/>
						</Link>
					</td>

					<td class="border-t">
						<Link class="flex items-center px-6 py-4" :href="`/products/${product.id}/edit`" tabindex="-1">
							{{ product.article_name }}
						</Link>
					</td>

					<td class="border-t px-6 py-4">
						{{ product.gcma }}
					</td>

					<td class="border-t px-6 py-4">
						<span
							class="text-xs font-semibold mr-2 px-2.5 py-0.5 rounded"
							:class="{
								'bg-green-100 text-green-800 dark:bg-green-200 dark:text-green-900': product.is_published,
								'bg-gray-100 text-gray-800  dark:bg-gray-700 dark:text-gray-300': !product.is_published,
							}"
						>
							{{ product.is_published ? __('Published') : __('Not Published') }}
						</span>
					</td>

					<td class="w-px border-t">
						<div class="flex items-center px-4">
							<Link class="flex items-center px-4" :href="`/products/${product.id}/edit`" tabindex="-1">
								<Icon name="cheveron-right" class="block h-6 w-6 fill-gray-400" />
							</Link>

							<a
								v-if="product.attachment && product.attachment.url"
								class="flex items-center px-2"
								:href="product.attachment.url" target="_blank" tabindex="-1"
							>
								<Icon name="pdf" class="block h-4 w-4 fill-gray-400" />
							</a>
						</div>
					</td>
				</tr>

				<tr v-if="products.length === 0">
					<td class="border-t px-6 py-4" colspan="4">{{ __('No products found.') }}</td>
				</tr>
			</table>
		</div>

		<Pagination class="mt-6" :links="products.links" />
	</div>
</template>

<script>
	import { Head, Link } from '@inertiajs/inertia-vue3'
	import Icon 		  from '@/Shared/Icon'
	import pickBy 		  from 'lodash/pickBy'
	import Layout 		  from '@/Shared/Layout'
	import throttle 	  from 'lodash/throttle'
	import mapValues 	  from 'lodash/mapValues'
	import SearchFilter   from '@/Shared/SearchFilter'
	import Pagination 	  from '@/Shared/Pagination'

	export default {
		components: {
			Head,
			Icon,
			Link,
			SearchFilter,
			Pagination,
		},
		layout: Layout,
		props: {
			filters: Object,
			products: [Object, Array],
		},
		data() {
			return {
				categories: [],
				form: {
					category: this.filters.category,
					search: this.filters.search,
					role: this.filters.role,
					trashed: this.filters.trashed,
				}
			}
		},
		computed: {
			parentCategories() {
				const items = []

				this.categories.forEach(item => {
					if (!item.parent_id) {
						items.push(item)
					}
				})

				if (items.length > 0) {
					const childs = this.categories.filter(c => c.parent_id !== null)

					items.forEach(parent => {
						parent['childs'] = childs.filter(c => c.parent_id === parent.id)
						parent.childs.unshift(parent)
					})
				}

				return items
			},
			ChildCategories() {
				return this.categories.filter(c => c.parent_id !== null)
			}
		},
		watch: {
			form: {
				deep: true,
				handler: throttle(function () {
					this.$inertia.get('/products', pickBy(this.form), {preserveState: true})
				}, 150)
			}
		},
		methods: {
			openAttachmentLink(product) {
				window.open(product.attachment.url, '_blank')
			},
			reset() {
				this.form = mapValues(this.form, () => null)
			},
			fetchCategories() {
				fetch('/api/categories?per_page=all').then((response) => response.json()).then(data => {
					this.categories = data.categories

					if (this.form.parent_id) {
						this.selectedParent = this.categories.find((category) => category.id === this.form.parent_id)
					}
				})
			},
		},
		mounted() {
			this.fetchCategories()
		}
	}
</script>
